import { urlBuildQueryParams } from 'pf-frontend-common/src/helper/url/build-query-params';
import { urlSerializeParams } from 'pf-frontend-common/src/helper/url/serialize-params';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { Component, h } from 'preact';

import { PreactStateInterface } from 'common/module/preact/state.interface';

import { PaginationComponentLinkInterface } from './component-link.interface';
import { PaginationComponentPropsInterface } from './component-props.interface';
import { PaginationLinkTemplate } from './link/template';

export class PaginationComponent extends Component<PaginationComponentPropsInterface, PreactStateInterface> {
  private cssClass: DataKeyValueStringInterface = {
    active: 'pagination__link--active',
    next: 'pagination__link--next',
    prev: 'pagination__link--prev',
  };

  /**
   * @inheritDoc
   */
  public render(): preact.JSX.Element | null {
    if (this.props.total <= this.props.limit) {
      return;
    }

    return (
      <div
        className={`
        pagination
        ${this.props.disabled ? 'pagination--disabled' : ''}
      `}
      >
        <div>{this.renderPrevLink()}</div>
        <div className='pagination__links'>{this.renderLinks()}</div>
        <div>{this.renderNextLink()}</div>
      </div>
    );
  }

  /**
   * Render previous link
   */
  private renderPrevLink(): preact.JSX.Element | null {
    if (this.props.currentPage <= 1) {
      return;
    }
    const link = this.getLinkParams(this.props.prevLinkTitle, this.props.currentPage - 1);

    return (
      <PaginationLinkTemplate
        className={this.cssClass.prev}
        link={link}
        showPrevIcon={true}
        onClickPage={this.props.onClickPage}
      />
    );
  }

  /**
   * Render other links like 1,2,3,...
   */
  private renderLinks(): preact.JSX.Element[] {
    const links = this.prepareLinks();

    return links.map((link) => {
      const classNames = this.props.currentPage === link.page ? this.cssClass.active : null;

      return <PaginationLinkTemplate className={classNames} link={link} onClickPage={this.props.onClickPage} />;
    });
  }

  /**
   * Render next link
   */
  private renderNextLink(): preact.JSX.Element | null {
    if (this.props.currentPage >= this.getPageCount()) {
      return;
    }
    const link = this.getLinkParams(this.props.nextLinkTitle, this.props.currentPage + 1);

    return (
      <PaginationLinkTemplate
        className={this.cssClass.next}
        link={link}
        onClickPage={this.props.onClickPage}
        showNextIcon={true}
      />
    );
  }

  /**
   * Get link params
   *
   * @param linkTitle - <a ...>{linkTitle}</a>
   */
  private getLinkParams(linkTitle: string, page: number): PaginationComponentLinkInterface {
    return {
      href: this.setHref(page),
      title: linkTitle,
      page,
    };
  }

  /**
   * Set href
   */
  private setHref(page: number): string {
    let href = this.props.href;

    if (page <= 1) {
      return href;
    }

    const pathArray = href.split('?');
    const params = urlBuildQueryParams(href);
    const pageKey = 'page';

    params[pageKey] = page;
    const queryString = urlSerializeParams(params);

    if (pathArray.length > 1) {
      pathArray[1] = queryString;
      href = pathArray.join('?');
    } else {
      href = '?' + queryString;
    }

    return href;
  }

  private getPageCount(): number {
    return Math.ceil(this.props.total / this.props.limit);
  }

  /**
   * Prepare other links
   */
  private prepareLinks(): PaginationComponentLinkInterface[] {
    let links = [];
    const halfAmountOfPages = Math.floor(this.props.amountOfPagesShown / 2);
    const pageCount = this.getPageCount();
    const maxPageIndex = pageCount - 1;

    for (let i = 1; i <= this.getPageCount(); i++) {
      links.push(this.getLinkParams(String(i), i));
    }

    if (this.props.currentPage <= halfAmountOfPages) {
      links = links.slice(0, this.props.amountOfPagesShown);
    } else {
      const maxIndex = this.props.currentPage + halfAmountOfPages - 1;
      let minIndex = this.props.currentPage - halfAmountOfPages - 1;
      if (maxIndex > maxPageIndex) {
        minIndex -= maxIndex - maxPageIndex;
      }
      if (this.props.amountOfPagesShown % 2 === 0) {
        minIndex = minIndex + 1;
      }
      minIndex = minIndex < 0 ? 0 : minIndex;
      links = links.slice(minIndex, maxIndex + 1);
    }

    return links;
  }
}
